import Button from "devextreme-react/button";
import React from "react";

// The collapsible bordered group the template tabs are built from: a header carrying the name, a
// short line saying how much is configured inside, and whatever buttons that tab needs, over a
// body that collapses away. Permissions, Actions and Transitions all render through this so the
// three tabs cannot drift apart.
type Props = {
    title: string;
    // Reads beside the title - what is set up in here, without opening it
    summary?: string;
    summaryColor?: string;
    // Tab-specific buttons, between the title and the expander
    headerRight?: React.ReactNode;
    collapsed: boolean;
    onToggle: () => void;
    // Marks a group staged for removal: red edge, faint wash, struck-through title
    removed?: boolean;
    testId?: string;
    toggleTestId?: string;
    children: React.ReactNode;
};

export const GROUP_REMOVED_COLOR = "#c9302c";
// A row struck through inside a group reads the same as a struck-through group title
export const removedTextStyle: React.CSSProperties = { textDecoration: "line-through", color: GROUP_REMOVED_COLOR };

export default function TemplateGroupBox({
    title,
    summary,
    summaryColor,
    headerRight,
    collapsed,
    onToggle,
    removed = false,
    testId,
    toggleTestId,
    children
}: Props) {
    return (
        <div
            data-testid={testId}
            data-removed={removed}
            style={{
                padding: "12px",
                border: "1px solid #e0e0e0",
                borderLeft: removed ? `3px solid ${GROUP_REMOVED_COLOR}` : "1px solid #e0e0e0",
                borderRadius: "4px",
                backgroundColor: removed ? "#fdf7f7" : undefined,
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: collapsed ? 0 : "12px" }}>
                <Button
                    icon={collapsed ? "chevronright" : "chevrondown"}
                    stylingMode="text"
                    hint={collapsed ? `Show ${title}` : `Hide ${title}`}
                    elementAttr={{ "data-testid": toggleTestId, "aria-expanded": !collapsed }}
                    onClick={onToggle}
                />
                <div
                    style={{ flex: 1, fontWeight: 600, fontSize: "14px", cursor: "pointer" }}
                    onClick={onToggle}
                >
                    <span style={removed ? { textDecoration: "line-through", color: GROUP_REMOVED_COLOR } : undefined}>
                        {title}
                    </span>
                    {summary && (
                        <span style={{
                            fontWeight: 400,
                            fontSize: "12px",
                            color: summaryColor ?? "#888",
                            marginLeft: "8px"
                        }}>
                            {summary}
                        </span>
                    )}
                </div>
                {headerRight}
            </div>

            {collapsed ? null : children}
        </div>
    );
}
